/**
 * Server-Sent Events (SSE) parsing utilities.
 *
 * Extracted from CLI ask.ts for shared use by CLI and TUI.
 */

import type {
  SSEStreamStartData,
  AgentStepEvent,
  UserMessageEvent,
  MessageQueuedEvent,
  MessageMetadataPayload,
  UserInputRequestEvent,
  ProbeAnswerEvent,
  DeferredInterjectionEvent,
  AgentControlStateEvent,
  AgentStepDeltaEvent,
  GeneratingEvent,
  ArtifactEvent,
  ResponseCreatedEvent,
  ResponseOutputTextDeltaEvent,
  ResponseOutputTextDoneEvent,
  ResponseOutputItemAddedEvent,
  ResponseOutputItemDoneEvent,
  ResponseContentPartAddedEvent,
  ResponseCompletedEvent,
  ResponseFailedEvent,
  ResponseUsage,
  TokenBudgetContext,
  ArbiErrorEvent,
} from './sse-types.js'

export interface SSEEvent {
  event: string
  data: string
}

/**
 * Last-resort label when the backend sent none: `tool_progress` → "Tool progress".
 *
 * There is deliberately no step→label map here. The backend owns display labels
 * (STEP_DISPLAY_META / TOOL_DISPLAY_META) and puts them on `label` for every
 * event; a mirrored copy on this side only ever drifts from it.
 */
function humanizeStep(identifier: string): string {
  const spaced = identifier.replace(/[_-]+/g, ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * A document-name lookup: `doc_ext_id` → human file name. Pass this to
 * `formatAgentStepLabel` so steps that target a specific document
 * ("Reading document", "Getting table of contents") render the real
 * file name instead of an opaque `doc-xxxxxxxx` id. Callers build it
 * from the workspace document list they already fetch before querying.
 */
export type DocNameMap = Record<string, string>

/**
 * Build a `DocNameMap` from a workspace document list.
 *
 * Every client that renders steps needs this map and was building it with
 * the same loop over `documents.listDocuments()`. It lives here so callers
 * inherit it with the formatter that consumes it, rather than each keeping
 * a copy that can drift. Documents missing an id or file name are skipped —
 * an unresolved ref renders as its raw id, which `resolveDocRefs` already
 * handles.
 */
export function buildDocNameMap(
  docs: ReadonlyArray<{ external_id?: unknown; file_name?: unknown }>
): DocNameMap {
  const map: DocNameMap = {}
  for (const doc of docs) {
    if (typeof doc.external_id === 'string' && typeof doc.file_name === 'string') {
      map[doc.external_id] = doc.file_name
    }
  }
  return map
}

/**
 * Replace `{doc-xxxxxxxx}` placeholders in a string with file names.
 *
 * The backend embeds these placeholders in step summaries/messages and
 * expects the client to resolve them against the workspace document
 * list (the web UI does the same). An unknown id is left as-is rather
 * than dropped, so the text never silently loses a reference.
 */
function resolveDocRefs(text: string, docNames: DocNameMap | undefined): string {
  if (!docNames) return text
  return text.replace(/\{(doc-[^}]+)\}/g, (_match, id: string) => docNames[id] ?? id)
}

/**
 * If a step targets a single document, return its display name.
 *
 * Tool-call steps carry their params in `detail[]`; a document-scoped
 * tool (read_document, get_table_of_contents) puts the id under
 * `doc_ext_id`. We resolve it through `docNames`, falling back to the
 * raw id only when the name isn't in the map. Steps that span many
 * documents (e.g. search) carry no single `doc_ext_id`, so they return
 * nothing and keep their plain label.
 */
function resolveStepDocName(
  step: AgentStepEvent,
  docNames: DocNameMap | undefined
): string | undefined {
  // Without a name map, leave the label clean rather than leaking a raw id.
  if (!docNames) return undefined
  const detail = step.detail as Array<Record<string, unknown>> | undefined
  if (!detail || detail.length === 0) return undefined
  for (const d of detail) {
    const id = d.doc_ext_id ?? d.document_ext_id
    if (typeof id === 'string') return docNames[id] ?? id
  }
  return undefined
}

/**
 * Extract a clean page range from a document-scoped step.
 *
 * The backend embeds the pages it read in the detail `summary` as e.g.
 * `Reading {doc-xxx} pp. 4.0–5.0, 10.0–11.0 +digest`. The numbers come
 * from the model's tool args and are sometimes fractional refs
 * (`4.10`, `2.5`) whose integer part is the page. We pull out the
 * `pp. …` clause, floor each endpoint to its whole page, collapse
 * `N–N` to `N`, drop duplicates, and discard markers like `+digest`.
 * The result ("pp. 2, 4–5") distinguishes repeated reads of the same
 * document and tells the user which pages were consulted — without the
 * chunk/digest jargon.
 */
function extractPageRange(step: AgentStepEvent): string | undefined {
  const detail = step.detail as Array<Record<string, unknown>> | undefined
  if (!detail || detail.length === 0) return undefined
  for (const d of detail) {
    const summary = typeof d.summary === 'string' ? d.summary : undefined
    if (!summary) continue
    // Capture the comma-separated range list after "p." / "pp.".
    const match = summary.match(/\bpp?\.\s*(\d[\d.,\s–—-]*)/)
    if (!match) continue
    // Floor every number to its integer page, then collapse "N–N" → "N".
    const floored = match[1]
      .replace(/\d+(?:\.\d+)?/g, (n) => String(Math.floor(parseFloat(n))))
      .replace(/(\d+)\s*[–—-]\s*\1(?!\d)/g, '$1')
    // Split into items, drop blanks and duplicates, and rejoin.
    const items: string[] = []
    for (const raw of floored.split(',')) {
      const item = raw.trim()
      if (item && !items.includes(item)) items.push(item)
    }
    if (items.length > 0) return `pp. ${items.join(', ')}`
  }
  return undefined
}

/**
 * Format an AgentStepEvent into a human-readable label.
 *
 * Uses backend-provided `label` field when available, falling back to
 * tool name or step name for old persisted events. Pass `docNames` to
 * resolve document-scoped steps to their file name.
 *
 * Priority:
 * 1. `focus` — the agent's descriptive sentence about what it's doing
 * 2. `label` — backend-provided display label (with doc name + pages
 *    appended when the step targets one document)
 * 3. For `tool_progress` — detail label + optional message
 * 4. Lifecycle fallback (planning, evaluation, etc.)
 * 5. Tool name from detail
 * 6. Fallback to step name or empty string
 *
 * The `goal` step's backend label is "Thinking…" (the bare "Goal" told
 * a user nothing); the fallback map covers older events that omit it.
 */
/**
 * Human label for a Responses output_item (function_call / reasoning / message).
 * Reads the backend-owned arbi extension: the agent's focus when present, else
 * the label. The backend owns all wording — the SDK never invents it.
 */
export function formatItemLabel(
  item: { type?: string; name?: string; arbi?: { label?: string; focus?: string } },
  docNames?: DocNameMap
): string {
  const arbi = item.arbi
  if (arbi?.focus) return resolveDocRefs(arbi.focus, docNames)
  if (arbi?.label) return arbi.label
  return item.name ?? ''
}

export function formatAgentStepLabel(step: AgentStepEvent, docNames?: DocNameMap): string {
  // Goal/Thinking step. The backend now labels this "Thinking…"; older
  // deployments still send the bare "Goal", which is opaque to a user.
  // Normalize both (and a missing label) to "Thinking…", while letting
  // any other custom label through. Transitional — the explicit "Goal"
  // check can be dropped once every backend has rolled out the new label.
  if (step.step === 'goal') {
    return step.label && step.label !== 'Goal' ? step.label : 'Thinking…'
  }

  if (step.focus) return resolveDocRefs(step.focus, docNames)

  const docName = resolveStepDocName(step, docNames)
  // A single-document step gets "— Name", plus "· pp. 4–5" when the
  // step read specific pages, so repeated reads of the same doc stay
  // distinct and informative.
  const pages = docName ? extractPageRange(step) : undefined
  const suffix = docName ? ` — ${docName}${pages ? ` · ${pages}` : ''}` : ''

  // Backend-provided label on the step.
  if (step.label) return `${step.label}${suffix}`

  const detail = step.detail as
    Array<{ tool?: string; label?: string; message?: string }> | undefined

  if (step.step === 'tool_progress' && detail && detail.length > 0) {
    const label =
      detail[0].label || (detail[0].tool && humanizeStep(detail[0].tool)) || humanizeStep(step.step)
    if (suffix) return `${label}${suffix}`
    const message = detail[0].message
    return message ? `${label}: ${resolveDocRefs(message, docNames)}` : label
  }

  if (step.step) {
    return humanizeStep(step.step)
  }

  if (detail && detail.length > 0) {
    if (detail[0].label) return `${detail[0].label}${suffix}`
    if (detail[0].tool) return detail[0].tool
  }

  return ''
}

/**
 * Parse SSE events from a chunk of text, combining with a buffer
 * of incomplete data from previous chunks.
 *
 * Returns parsed events and any remaining incomplete data.
 */
export function parseSSEEvents(
  chunk: string,
  buffer: string
): { events: SSEEvent[]; remaining: string } {
  const combined = buffer + chunk
  const events: SSEEvent[] = []
  const parts = combined.split('\n\n')
  const remaining = parts.pop() || ''

  for (const part of parts) {
    if (!part.trim()) continue
    let eventType = ''
    let eventData = ''
    for (const line of part.split('\n')) {
      if (line.startsWith('event: ')) eventType = line.slice(7).trim()
      else if (line.startsWith('data: ')) eventData = line.slice(6)
    }
    if (eventType && eventData) events.push({ event: eventType, data: eventData })
  }

  return { events, remaining }
}

/**
 * Callbacks for streaming SSE events. All callbacks are optional —
 * omitted callbacks simply ignore that event type.
 */

export interface SSEStreamCallbacks {
  onStreamStart?: (data: SSEStreamStartData) => void
  onToken?: (content: string) => void
  /** Answer/narration text delta with its message item id, so the client can
   * attribute each burst to the right message (narration vs the answer). */
  onOutputTextDelta?: (data: { item_id: string; delta: string }) => void
  /** Live reasoning/thinking-token deltas (raw channel). Usually surfaced only in message details. */
  onReasoningDelta?: (delta: string) => void
  onTextDone?: (text: string) => void
  onOutputItemAdded?: (data: ResponseOutputItemAddedEvent) => void
  onOutputItemDone?: (data: ResponseOutputItemDoneEvent) => void
  /** A function_call item's JSON arguments streaming in. */
  onFunctionCallArgumentsDelta?: (data: { item_id: string; delta: string }) => void
  onContentPartAdded?: (data: ResponseContentPartAddedEvent) => void
  onError?: (message: string) => void
  /**
   * Fired when the server emits a structured `arbi.error` event mid-stream.
   * Backend uses this for quota-exceeded errors so clients can render an
   * upgrade modal without losing the surrounding stream context.
   */
  onArbiError?: (event: ArbiErrorEvent) => void
  onMessageQueued?: (data: MessageQueuedEvent) => void
  onUserMessage?: (data: UserMessageEvent) => void
  onMetadata?: (data: MessageMetadataPayload) => void
  onUserInputRequest?: (data: UserInputRequestEvent) => void
  /** Out-of-band status probe answer ("how's it going?") — transient, never persisted. */
  onProbeAnswer?: (data: ProbeAnswerEvent) => void
  /**
   * Steering that arrived after the agent had committed to its answer — too late
   * to steer this turn. The consumer submits `message` as a NEW turn.
   */
  onDeferredInterjection?: (data: DeferredInterjectionEvent) => void
  /** Server-confirmed run-control state (paused / running). */
  onAgentControlState?: (data: AgentControlStateEvent) => void
  onArtifact?: (data: ArtifactEvent) => void
  onElapsedTime?: (t: number) => void
  onUsage?: (usage: ResponseUsage) => void
  /** Final per-turn token/credit budget snapshot from response.completed. */
  onContextUpdate?: (context: TokenBudgetContext) => void
  onComplete?: () => void
}

/**
 * Result returned after the SSE stream is fully consumed.
 */
export interface SSEStreamResult {
  text: string
  assistantMessageExtId: string | null
  agentSteps: string[]
  /** Total number of tool calls across all agent steps. */
  toolCallCount: number
  errors: string[]
  userMessage: UserMessageEvent | null
  metadata: MessageMetadataPayload | null
  artifacts: ArtifactEvent[]
  usage: ResponseUsage | null
  /** Token budget context snapshot from response.completed. */
  context: TokenBudgetContext | null
}

/**
 * Stream SSE events from a Response, invoking callbacks as events arrive.
 *
 * This is the primary streaming function — use it when you need real-time
 * event handling (CLI streaming to stdout, TUI rendering, etc.).
 *
 * Also accumulates the full response and returns it, so callers can use
 * both the streaming callbacks and the final result.
 */
export async function streamSSE(
  response: Response,
  callbacks: SSEStreamCallbacks = {}
): Promise<SSEStreamResult> {
  if (!response.body) {
    throw new Error('No response body')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let sseBuffer = ''
  let text = ''
  let assistantMessageExtId: string | null = null
  const agentSteps: string[] = []
  let toolCallCount = 0
  const errors: string[] = []
  const artifacts: ArtifactEvent[] = []
  let userMessage: UserMessageEvent | null = null
  let metadata: MessageMetadataPayload | null = null
  let usage: ResponseUsage | null = null
  let context: TokenBudgetContext | null = null

  // Each handler receives the raw JSON string and parses to the correct type.
  // The SSE event names the backend emits (the `event:` line). Hand-maintained
  // to mirror the backend emitters; `satisfies` below turns a missing/typo'd/
  // extra handler into a compile error. Raw token channels (e.g. `reasoning`)
  // are NOT events — handled separately in processEvents. A brand-new backend
  // event name surfaces as a runtime warn there until it's added here.
  type ArbiSseEventName =
    | 'response.created'
    | 'response.output_item.added'
    | 'response.content_part.added'
    | 'response.output_text.delta'
    | 'response.output_text.done'
    | 'response.output_item.done'
    | 'response.function_call_arguments.delta'
    | 'response.completed'
    | 'response.failed'
    | 'arbi.message_queued'
    | 'arbi.user_message'
    | 'arbi.message_metadata'
    | 'arbi.user_input_request'
    | 'arbi.probe_answer'
    | 'arbi.deferred_interjection'
    | 'arbi.agent_control_state'
    | 'arbi.artifact'
    | 'arbi.error'

  const eventHandlers = {
    // OpenAI Responses API events (dot-separated names from server)
    'response.created': (raw) => {
      const data: ResponseCreatedEvent = JSON.parse(raw)
      const id = data.response?.id
      if (id) assistantMessageExtId = id
      callbacks.onStreamStart?.({ assistant_message_ext_id: id })
      if (data.t != null) callbacks.onElapsedTime?.(data.t)
    },
    'response.output_item.added': (raw) => {
      const data: ResponseOutputItemAddedEvent = JSON.parse(raw)
      callbacks.onOutputItemAdded?.(data)
    },
    'response.content_part.added': (raw) => {
      const data: ResponseContentPartAddedEvent = JSON.parse(raw)
      callbacks.onContentPartAdded?.(data)
    },
    'response.output_text.delta': (raw) => {
      const data: ResponseOutputTextDeltaEvent = JSON.parse(raw)
      if (data.delta) {
        text += data.delta
        // Deliver each delta to EXACTLY ONE sink. onOutputTextDelta is the
        // item-aware v3 callback; onToken is the legacy plain-text stream. A
        // consumer that wires BOTH (the web app does) would otherwise append the
        // same delta twice — the streamed answer visibly doubles token-by-token.
        if (callbacks.onOutputTextDelta) {
          callbacks.onOutputTextDelta({ item_id: data.item_id ?? '', delta: data.delta })
        } else {
          callbacks.onToken?.(data.delta)
        }
      }
    },
    'response.output_text.done': (raw) => {
      const data: ResponseOutputTextDoneEvent = JSON.parse(raw)
      text = data.text
      callbacks.onTextDone?.(data.text)
    },
    'response.function_call_arguments.delta': (raw) => {
      const data = JSON.parse(raw)
      callbacks.onFunctionCallArgumentsDelta?.(data)
    },
    'response.output_item.done': (raw) => {
      const data: ResponseOutputItemDoneEvent = JSON.parse(raw)
      const item = data.item as {
        type?: string
        arbi?: { label?: string; focus?: string }
        name?: string
      }
      // Build the run summary from the trace items (function_call / reasoning);
      // message items are the answer, not steps.
      if (item?.type === 'function_call' || item?.type === 'reasoning') {
        const label = formatItemLabel(item)
        if (label) agentSteps.push(label)
        if (item.type === 'function_call') toolCallCount += 1
      }
      callbacks.onOutputItemDone?.(data)
      if ((data as { t?: number }).t != null) callbacks.onElapsedTime?.((data as { t?: number }).t!)
    },
    'response.completed': (raw) => {
      const data: ResponseCompletedEvent = JSON.parse(raw)
      if (data.response?.usage) {
        usage = data.response.usage
        callbacks.onUsage?.(data.response.usage)
      }
      // Extract metadata from response.completed envelope (backend embeds it here)
      if (data.response?.metadata) {
        const meta = data.response.metadata as MessageMetadataPayload
        metadata = meta
        callbacks.onMetadata?.(meta)
      }
      const finalText = data.response?.metadata?.content
      if (finalText) {
        text = finalText
        callbacks.onTextDone?.(finalText)
      }
      // Capture token budget context snapshot
      if (data.context) {
        context = data.context
        callbacks.onContextUpdate?.(data.context)
      }
      if (data.t != null) callbacks.onElapsedTime?.(data.t)
      callbacks.onComplete?.()
    },
    'response.failed': (raw) => {
      const data: ResponseFailedEvent = JSON.parse(raw)
      const message = data.response?.error?.message || 'Unknown streaming error'
      errors.push(message)
      callbacks.onError?.(message)
    },
    // ARBI-specific events (dot-prefixed from server)
    'arbi.message_queued': (raw) => {
      const data: MessageQueuedEvent = JSON.parse(raw)
      // For continuation queries, the queued event includes the full user message
      // with status="queued" so the frontend can display it before the gate opens.
      if (data.user_message) {
        userMessage = data.user_message
        callbacks.onUserMessage?.(data.user_message)
      }
      callbacks.onMessageQueued?.(data)
    },
    'arbi.user_message': (raw) => {
      const data: UserMessageEvent = JSON.parse(raw)
      userMessage = data
      callbacks.onUserMessage?.(data)
    },
    'arbi.message_metadata': (raw) => {
      const data: MessageMetadataPayload = JSON.parse(raw)
      metadata = data
      callbacks.onMetadata?.(data)
    },
    'arbi.user_input_request': (raw) => {
      const data: UserInputRequestEvent = JSON.parse(raw)
      callbacks.onUserInputRequest?.(data)
    },
    'arbi.probe_answer': (raw) => {
      const data: ProbeAnswerEvent = JSON.parse(raw)
      callbacks.onProbeAnswer?.(data)
    },
    'arbi.deferred_interjection': (raw) => {
      const data: DeferredInterjectionEvent = JSON.parse(raw)
      callbacks.onDeferredInterjection?.(data)
    },
    'arbi.agent_control_state': (raw) => {
      const data: AgentControlStateEvent = JSON.parse(raw)
      callbacks.onAgentControlState?.(data)
    },
    'arbi.artifact': (raw) => {
      const data: ArtifactEvent = JSON.parse(raw)
      artifacts.push(data)
      callbacks.onArtifact?.(data)
    },
    'arbi.error': (raw) => {
      const data: ArbiErrorEvent = JSON.parse(raw)
      const message =
        typeof data?.message === 'string' && data.message.length > 0 ? data.message : data.code
      errors.push(message)
      callbacks.onArbiError?.(data)
      // Also surface as a generic error string for callers that only wired
      // up the legacy onError path — keeps compatibility while migrating.
      callbacks.onError?.(message)
    },
  } satisfies Record<ArbiSseEventName, (raw: string) => void>

  const processEvents = (events: SSEEvent[]) => {
    for (const { event, data } of events) {
      try {
        // Raw token channels are not typed protocol events — handle separately.
        if (event === 'reasoning') {
          callbacks.onReasoningDelta?.(data)
          continue
        }
        const handler = (eventHandlers as Record<string, (raw: string) => void>)[event]
        if (handler) {
          handler(data)
        } else {
          // Unknown event name — surface backend/SDK drift instead of silently dropping.
          console.warn(`[arbi-sse] unhandled SSE event: ${event}`)
        }
      } catch {
        /* skip unparseable events */
      }
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    const { events, remaining } = parseSSEEvents(decoder.decode(value, { stream: true }), sseBuffer)
    sseBuffer = remaining
    processEvents(events)
  }

  // Process any remaining buffered data
  if (sseBuffer.trim()) {
    const { events } = parseSSEEvents(sseBuffer + '\n\n', '')
    processEvents(events)
  }

  return {
    text,
    assistantMessageExtId,
    agentSteps,
    toolCallCount,
    errors,
    userMessage,
    metadata,
    artifacts,
    usage,
    context,
  }
}

/**
 * Format a human-readable summary line from a completed SSE stream result.
 *
 * Returns a string like:
 *   "3 steps (2 tool calls) · 1,234 tokens · 500/8,000 context · 12.40/50 credits · 2.5s"
 * (the credits segment is omitted when the workspace has no turn credit budget configured)
 *
 * Returns empty string if there's nothing to report.
 */
export function formatStreamSummary(result: SSEStreamResult, elapsedTime?: number | null): string {
  const parts: string[] = []

  if (result.agentSteps.length > 0) {
    let stepLabel = `${result.agentSteps.length} step${result.agentSteps.length === 1 ? '' : 's'}`
    if (result.toolCallCount > 0) {
      stepLabel += ` (${result.toolCallCount} tool call${result.toolCallCount === 1 ? '' : 's'})`
    }
    parts.push(stepLabel)
  }

  if (result.usage) {
    parts.push(`${result.usage.total_tokens.toLocaleString()} tokens`)
  }

  if (result.context && result.context.context_window > 0) {
    const used = result.context.all_llm_calls?.last_input_tokens ?? result.context.total_input
    parts.push(`${used.toLocaleString()}/${result.context.context_window.toLocaleString()} context`)
  }

  // AI credits spent this turn (shared across the main agent and every child it
  // spawns) — only shown when the workspace has a budget configured
  // (AGENT_TURN_CREDIT_BUDGET > 0); absent entirely when uncapped.
  if (result.context?.credits_spent != null && result.context.credit_budget != null) {
    parts.push(`${result.context.credits_spent.toFixed(2)}/${result.context.credit_budget} credits`)
  }

  if (elapsedTime != null) {
    parts.push(`${elapsedTime.toFixed(1)}s`)
  }

  return parts.length > 0 ? parts.join(' · ') : ''
}

/**
 * Consume an entire SSE stream without streaming callbacks.
 * Convenience alias for `streamSSE(response)`.
 */
export const consumeSSEStream = streamSSE
